import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { stateAbbreviations } from "@/components/USAMapNavigator";
import { withScenario } from "@/lib/mapNav";
import type { Hospital } from "@/types/map";

interface UseGpsLocationOptions {
  hospitals: Hospital[];
  flyToLocation?: (lat: number, lng: number) => void;
  setSelectedTableState: (s: string) => void;
  setSelectedTableCity: (s: string) => void;
  stopOrbit: () => void;
}

export function useGpsLocation({
  hospitals,
  flyToLocation: flyToLocationCb,
  setSelectedTableState,
  setSelectedTableCity,
  stopOrbit,
}: UseGpsLocationOptions) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [nearbyHospitals, setNearbyHospitals] = useState<Array<Hospital & { distance: number }>>([]);
  const [isLocatingGps, setIsLocatingGps] = useState(true);
  const [showEROnly, setShowEROnly] = useState(false);
  const [showUrgentCareOnly, setShowUrgentCareOnly] = useState(false);
  const [sortByMarkup, setSortByMarkup] = useState<'asc' | 'desc' | 'none'>('none');

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Auto-locate on page load
  useEffect(() => {
    if (hospitals.length > 0 && nearbyHospitals.length === 0) {
      if (navigator.geolocation) {
        setIsLocatingGps(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });

            const hospitalsWithDistance = hospitals.map(hospital => ({
              ...hospital,
              distance: calculateDistance(latitude, longitude, hospital.latitude, hospital.longitude)
            })).sort((a, b) => a.distance - b.distance).slice(0, 10);

            setNearbyHospitals(hospitalsWithDistance);
            setIsLocatingGps(false);
          },
          (error) => {
            console.log("Geolocation not available:", error);
            setIsLocatingGps(false);
          }
        );
      } else {
        setIsLocatingGps(false);
      }
    }
  }, [hospitals]); // eslint-disable-line react-hooks/exhaustive-deps

  const findNearMe = async () => {
    if (!navigator.geolocation) {
      toast({
        title: "Geolocation not supported",
        description: "Your browser doesn't support geolocation.",
        variant: "destructive",
      });
      return;
    }

    // Check permissions API if available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

        if (permissionStatus.state === 'denied') {
          toast({
            title: "Location access blocked",
            description: "Please click the location icon in your browser's address bar and allow location access.",
            variant: "destructive",
          });
          return;
        }
      } catch (e) {
        console.log("Permissions API not available:", e);
      }
    }

    // Check for HTTPS requirement
    const isLocalhost = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname.startsWith('192.168.');

    if (window.location.protocol !== 'https:' && !isLocalhost) {
      toast({
        title: "HTTPS Required",
        description: "Location services require a secure connection (HTTPS).",
        variant: "destructive",
      });
      return;
    }

    setIsLocatingGps(true);
    toast({
      title: "Finding your location...",
      description: "Please allow location access when prompted.",
    });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await response.json();
          const state = data.address?.state;

          if (state) {
            const stateEntry = Object.entries(stateAbbreviations).find(
              ([name]) => name.toLowerCase() === state.toLowerCase()
            );
            const stateCode = stateEntry ? stateEntry[1] : null;

            if (stateCode) {
              setSelectedTableState(stateCode);
              setSelectedTableCity('');
              navigate(withScenario(`/map/${stateCode.toLowerCase()}`), { replace: true });
              stopOrbit();

              setTimeout(() => {
                if (flyToLocationCb) {
                  flyToLocationCb(latitude, longitude);
                }
              }, 500);

              setTimeout(() => {
                const hospitalsWithDistance = hospitals.map(hospital => ({
                  ...hospital,
                  distance: calculateDistance(latitude, longitude, hospital.latitude, hospital.longitude)
                })).sort((a, b) => a.distance - b.distance).slice(0, 10);

                setNearbyHospitals(hospitalsWithDistance);
                setIsLocatingGps(false);

                toast({
                  title: "Location found",
                  description: `Showing hospitals in ${state}`,
                });
              }, 1500);
            }
          }
        } catch (error) {
          console.error("Reverse geocoding failed:", error);
          // Fallback: just fly to location
          stopOrbit();
          if (flyToLocationCb) {
            flyToLocationCb(latitude, longitude);
          }
          toast({
            title: "Location found",
            description: "Could not determine your state. Showing current location.",
          });
          setIsLocatingGps(false);
        }
      },
      (error) => {
        console.error("❌ Geolocation error:", error);
        let errorMessage = "Unable to get your location.";
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location access denied. Please enable location permissions in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable. Please check your device settings.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out. Please try again.";
            break;
        }
        setIsLocatingGps(false);
        toast({
          title: "Location error",
          description: errorMessage,
          variant: "destructive",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return {
    userLocation,
    nearbyHospitals,
    isLocatingGps,
    showEROnly,
    setShowEROnly,
    showUrgentCareOnly,
    setShowUrgentCareOnly,
    sortByMarkup,
    setSortByMarkup,
    findNearMe,
    calculateDistance,
  };
}
